import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Sparkles, BookOpen, Video, FileText, Download, ExternalLink, HelpCircle, Calculator, TrendingDown } from "lucide-react";

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://www.machinerymarketed.com/app-logo.png" 
            alt="MachineryMarketed Logo" 
            className="h-10 w-10"
          />
          <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <Link to="/" className="btn-outline text-sm">Back to Home</Link>
      </div>
    </nav>
  );
};

// Hidden Cost Calculator Component
const HiddenCostCalculator = () => {
  const [avgValue, setAvgValue] = useState(100000);
  const [numMachines, setNumMachines] = useState(50);
  const [floorplanRate, setFloorplanRate] = useState(8);
  const [deprecRate, setDeprecRate] = useState(1.5);
  const [insuranceRate, setInsuranceRate] = useState(1);
  const [result, setResult] = useState(null);
  const [animatedDaily, setAnimatedDaily] = useState(0);

  const calculateCost = () => {
    const value = parseFloat(avgValue) || 100000;
    const num = parseInt(numMachines) || 1;
    const floorplan = (parseFloat(floorplanRate) || 8) / 100;
    const deprec = (parseFloat(deprecRate) || 1.5) / 100;
    const insurance = (parseFloat(insuranceRate) || 1) / 100;

    const dailyInterest = (value * floorplan) / 365;
    const dailyDeprec = (value * deprec) / 365;
    const dailyIns = (value * insurance) / 365;
    const dailyYard = 10;

    const dailyPerMachine = dailyInterest + dailyDeprec + dailyIns + dailyYard;
    const dailyTotal = dailyPerMachine * num;
    const monthly = dailyTotal * 30;
    const yearly = dailyTotal * 365;

    setResult({
      dailyTotal,
      monthly,
      yearly,
      dailyInterest: Math.round(dailyInterest),
      dailyDeprec: Math.round(dailyDeprec),
      dailyIns: Math.round(dailyIns),
      dailyYard
    });

    // Animate count-up
    let count = 0;
    const target = Math.round(dailyTotal);
    const step = Math.ceil(target / 60) || 1;
    const interval = setInterval(() => {
      count += step;
      if (count >= target) {
        count = target;
        clearInterval(interval);
      }
      setAnimatedDaily(count);
    }, 16);
  };

  return (
    <section id="hidden-cost-calculator" className="py-16" style={{ background: "#0a1f44" }}>
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-4xl mx-auto rounded-2xl p-6 md:p-12" style={{ 
          background: "linear-gradient(135deg, #0d2647 0%, #0a1f44 100%)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
          border: "1px solid rgba(96, 165, 250, 0.2)"
        }}>
          {/* Header */}
          <div className="text-center mb-10">
            <h2 className="font-bold mb-6" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "#60a5fa", fontSize: "clamp(1.8rem, 5vw, 2.4rem)" }}>
              The Hidden Cost Calculator
            </h2>
            <p className="max-w-2xl mx-auto" style={{ color: "#e0e7ff", lineHeight: "1.6", fontSize: "clamp(1rem, 4vw, 1.1rem)" }}>
              Unlisted machines quietly rot profits daily: floorplan interest, depreciation, insurance, yard costs.
            </p>
            <p className="mt-4" style={{ color: "#93c5fd" }}>
              Enter your numbers for a no-BS estimate of the leakage.
            </p>
          </div>

          {/* Calculator Form */}
          <div className="max-w-xl mx-auto mb-10 px-2">
            <div className="space-y-7">
              {/* Average Value */}
              <div>
                <label className="block mb-2 font-semibold" style={{ color: "#e0e7ff", fontSize: "clamp(1rem, 3.5vw, 1.1rem)" }}>
                  Avg machine value ($)
                </label>
                <input
                  type="number"
                  min="10000"
                  step="1000"
                  value={avgValue}
                  onChange={(e) => setAvgValue(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg"
                  style={{ 
                    background: "#13294b", 
                    border: "1px solid #4b6cb7", 
                    color: "white",
                    fontSize: "clamp(1rem, 4vw, 1.1rem)"
                  }}
                />
              </div>

              {/* Number of Machines */}
              <div>
                <label className="block mb-2 font-semibold" style={{ color: "#e0e7ff", fontSize: "clamp(1rem, 3.5vw, 1.1rem)" }}>
                  Unlisted / slow-moving machines
                </label>
                <input
                  type="number"
                  min="1"
                  value={numMachines}
                  onChange={(e) => setNumMachines(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg"
                  style={{ 
                    background: "#13294b", 
                    border: "1px solid #4b6cb7", 
                    color: "white",
                    fontSize: "clamp(1rem, 4vw, 1.1rem)"
                  }}
                />
              </div>

              {/* Floorplan Rate with Slider */}
              <div>
                <label className="block mb-2 font-semibold" style={{ color: "#e0e7ff", fontSize: "clamp(1rem, 3.5vw, 1.1rem)" }}>
                  Floorplan interest (% p.a.) – 5–15%
                </label>
                <div className="flex items-center gap-3 flex-col sm:flex-row">
                  <input
                    type="range"
                    min="5"
                    max="15"
                    step="0.5"
                    value={floorplanRate}
                    onChange={(e) => setFloorplanRate(e.target.value)}
                    className="flex-1 w-full"
                    style={{ accentColor: "#60a5fa", height: "10px" }}
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="5"
                      max="15"
                      step="0.5"
                      value={floorplanRate}
                      onChange={(e) => setFloorplanRate(e.target.value)}
                      className="px-3 py-2 rounded-lg text-center"
                      style={{ 
                        background: "#13294b", 
                        border: "1px solid #4b6cb7", 
                        color: "white",
                        width: "70px"
                      }}
                    />
                    <span style={{ color: "#e0e7ff" }}>%</span>
                  </div>
                </div>
              </div>

              {/* Depreciation Rate with Slider */}
              <div>
                <label className="block mb-2 font-semibold" style={{ color: "#e0e7ff", fontSize: "clamp(1rem, 3.5vw, 1.1rem)" }}>
                  Depreciation (% p.a.) – 1–20%
                </label>
                <div className="flex items-center gap-3 flex-col sm:flex-row">
                  <input
                    type="range"
                    min="1"
                    max="20"
                    step="0.5"
                    value={deprecRate}
                    onChange={(e) => setDeprecRate(e.target.value)}
                    className="flex-1 w-full"
                    style={{ accentColor: "#60a5fa", height: "10px" }}
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="20"
                      step="0.5"
                      value={deprecRate}
                      onChange={(e) => setDeprecRate(e.target.value)}
                      className="px-3 py-2 rounded-lg text-center"
                      style={{ 
                        background: "#13294b", 
                        border: "1px solid #4b6cb7", 
                        color: "white",
                        width: "70px"
                      }}
                    />
                    <span style={{ color: "#e0e7ff" }}>%</span>
                  </div>
                </div>
              </div>

              {/* Insurance Rate with Slider */}
              <div>
                <label className="block mb-2 font-semibold" style={{ color: "#e0e7ff", fontSize: "clamp(1rem, 3.5vw, 1.1rem)" }}>
                  Insurance (% p.a.) – Typical 1–2.5% for ag gear—check your policy
                </label>
                <div className="flex items-center gap-3 flex-col sm:flex-row">
                  <input
                    type="range"
                    min="0.5"
                    max="3"
                    step="0.1"
                    value={insuranceRate}
                    onChange={(e) => setInsuranceRate(e.target.value)}
                    className="flex-1 w-full"
                    style={{ accentColor: "#60a5fa", height: "10px" }}
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0.5"
                      max="3"
                      step="0.1"
                      value={insuranceRate}
                      onChange={(e) => setInsuranceRate(e.target.value)}
                      className="px-3 py-2 rounded-lg text-center"
                      style={{ 
                        background: "#13294b", 
                        border: "1px solid #4b6cb7", 
                        color: "white",
                        width: "70px"
                      }}
                    />
                    <span style={{ color: "#e0e7ff" }}>%</span>
                  </div>
                </div>
              </div>

              <button
                onClick={calculateCost}
                className="w-full py-4 rounded-lg font-bold transition-all hover:scale-[1.02]"
                style={{ 
                  background: "#3b82f6", 
                  color: "white",
                  fontSize: "clamp(1.2rem, 5vw, 1.4rem)",
                  boxShadow: "0 4px 12px rgba(59, 130, 246, 0.4)"
                }}
              >
                Calculate My Daily Rot
              </button>
            </div>
          </div>

          {/* Results */}
          {result && (
            <div className="animate-fade-in">
              {/* Big Daily Number */}
              <div className="text-center mb-8">
                <h3 className="font-black mb-2" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "#f87171", fontSize: "clamp(2rem, 7vw, 2.8rem)" }}>
                  ${animatedDaily.toLocaleString()} / day
                </h3>
              </div>

              {/* Monthly/Annual */}
              <div className="text-center mb-10">
                <p style={{ color: "#e0e7ff", fontSize: "clamp(1.2rem, 4.5vw, 1.4rem)" }}>
                  ≈ <strong>${Math.round(result.monthly).toLocaleString()}</strong> / month
                </p>
                <p style={{ color: "#e0e7ff", fontSize: "clamp(1.2rem, 4.5vw, 1.4rem)" }}>
                  ≈ <strong>${Math.round(result.yearly).toLocaleString()}</strong> / year
                </p>
              </div>

              {/* Breakdown */}
              <div className="rounded-xl p-6 mb-8 mx-auto" style={{ background: "#1e3a8a", maxWidth: "500px" }}>
                <h4 className="font-bold text-center mb-4" style={{ color: "#93c5fd", fontSize: "clamp(1.2rem, 4vw, 1.4rem)" }}>
                  Breakdown (per machine, daily)
                </h4>
                <ul className="space-y-2" style={{ color: "#e0e7ff", fontSize: "clamp(1rem, 3.5vw, 1.1rem)", lineHeight: "2.2" }}>
                  <li className="flex justify-between">
                    <span>Floorplan interest:</span>
                    <strong>${result.dailyInterest.toLocaleString()}</strong>
                  </li>
                  <li className="flex justify-between">
                    <span>Depreciation:</span>
                    <strong>${result.dailyDeprec.toLocaleString()}</strong>
                  </li>
                  <li className="flex justify-between">
                    <span>Insurance:</span>
                    <strong>${result.dailyIns.toLocaleString()}</strong>
                  </li>
                  <li className="flex justify-between">
                    <span>Yard & other:</span>
                    <strong>${result.dailyYard}</strong>
                  </li>
                </ul>
              </div>

              {/* CTA */}
              <div className="text-center">
                <p className="mb-4" style={{ color: "#bfdbfe", fontSize: "clamp(1rem, 4vw, 1.1rem)", lineHeight: "1.6" }}>
                  Redirect even part of this rot to new stock or bonuses.
                </p>
                <p className="mb-6" style={{ color: "#93c5fd" }}>
                  Stop the leakage—our app gets every machine visible & moving fast.
                </p>
                <Link 
                  to="/#contact"
                  className="inline-block px-8 py-4 rounded-lg font-bold text-lg transition-all hover:scale-105"
                  style={{ background: "#3b82f6", color: "white" }}
                >
                  Get MachineryMarketed Now →
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// Resource Card Component
const ResourceCard = ({ icon: Icon, title, description, type, image }) => (
  <div className="card overflow-hidden group" style={{ background: "var(--background-200)" }}>
    <div className="h-48 overflow-hidden relative">
      <img 
        src={image} 
        alt={title}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        loading="lazy"
      />
      <div className="absolute top-4 left-4">
        <span 
          className="px-3 py-1 rounded-full text-xs font-semibold"
          style={{ background: "var(--primary-100)", color: "white" }}
        >
          {type}
        </span>
      </div>
    </div>
    <div className="p-6">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "var(--primary-100)" }}>
          <Icon size={24} style={{ color: "white" }} />
        </div>
        <div>
          <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            {title}
          </h3>
          <p className="text-sm mb-4" style={{ color: "var(--text-200)" }}>{description}</p>
          <button className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--primary-300)" }}>
            Coming Soon <ExternalLink size={14} />
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Main Resources Page Component
const ResourcesPage = () => {
  const resources = [
    {
      icon: Video,
      type: "Video Tutorial",
      title: "Getting Started Guide",
      description: "Learn the basics of MachineryMarketed in under 15 minutes.",
      image: "https://www.machinerymarketed.com/app-create-account.jpg"
    },
    {
      icon: BookOpen,
      type: "Documentation",
      title: "Complete User Manual",
      description: "Detailed documentation for every feature and workflow.",
      image: "https://www.machinerymarketed.com/app-appraisal.jpg"
    },
    {
      icon: FileText,
      type: "Case Study",
      title: "Dealership Success Stories",
      description: "See how other dealerships are transforming their sales process.",
      image: "https://www.machinerymarketed.com/tractor_loader_pair.jpg"
    },
    {
      icon: Download,
      type: "Download",
      title: "Mobile App Setup",
      description: "iOS and Android app installation and configuration guide.",
      image: "https://www.machinerymarketed.com/app-machine-details.jpg"
    },
    {
      icon: HelpCircle,
      type: "FAQ",
      title: "Common Questions",
      description: "Quick answers to the most frequently asked questions.",
      image: "https://www.machinerymarketed.com/agri-4.jpg"
    },
    {
      icon: Video,
      type: "Webinar",
      title: "Advanced Features Training",
      description: "Deep dive into advanced workflows and integrations.",
      image: "https://www.machinerymarketed.com/app-valuation.jpg"
    }
  ];

  return (
    <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
      <Navigation />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative py-20">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://www.machinerymarketed.com/hero_excavator.jpg" 
              alt="Construction machinery"
              className="w-full h-full object-cover"
              style={{ opacity: 0.2 }}
              loading="eager"
            />
            <div 
              className="absolute inset-0"
              style={{ background: "linear-gradient(to bottom, rgba(0,12,37,0.8) 0%, var(--background-100) 100%)" }}
            />
          </div>
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6"
                style={{ background: "rgba(15, 95, 255, 0.2)", border: "1px solid rgba(15, 95, 255, 0.3)" }}
              >
                <Sparkles size={18} style={{ color: "var(--primary-300)" }} />
                <span style={{ color: "var(--text-100)" }}>Help Center</span>
              </div>
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-8"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                Resources & <span className="text-gradient">Support</span>
              </h1>
              <p className="text-xl max-w-3xl mx-auto" style={{ color: "var(--text-200)" }}>
                Everything you need to get the most out of MachineryMarketed. 
                Guides, tutorials, and support at your fingertips.
              </p>
            </div>
          </div>
        </section>

        {/* Hidden Cost Calculator */}
        <HiddenCostCalculator />

        {/* Quick Links */}
        <section className="py-12" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: BookOpen, label: "Documentation", href: "#docs" },
                { icon: Video, label: "Video Tutorials", href: "#videos" },
                { icon: HelpCircle, label: "FAQ", href: "/faq" },
                { icon: Download, label: "Downloads", href: "#downloads" }
              ].map((link, index) => (
                <Link
                  key={index}
                  to={link.href}
                  className="card p-6 text-center transition-all hover:scale-105"
                  style={{ background: "var(--background-100)" }}
                >
                  <link.icon size={32} style={{ color: "var(--primary-300)", margin: "0 auto 12px" }} />
                  <span className="font-medium" style={{ color: "var(--text-100)" }}>{link.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Resources Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
              Featured Resources
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {resources.map((resource, index) => (
                <ResourceCard key={index} {...resource} />
              ))}
            </div>
          </div>
        </section>

        {/* Coming Soon Banner */}
        <section className="py-20" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                More Resources Coming Soon
              </h2>
              <p className="mb-8" style={{ color: "var(--text-200)" }}>
                We're building out our resource library with video tutorials, case studies, 
                and comprehensive documentation. Check back soon for updates.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/faq" className="btn-primary">Visit FAQ</Link>
                <Link to="/how-it-works" className="btn-outline">How It Works</Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ background: "var(--background-200)", borderTop: "1px solid rgba(176, 176, 176, 0.1)" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://www.machinerymarketed.com/app-logo.png" 
                alt="MachineryMarketed Logo" 
                className="h-10 w-10"
              />
              <span className="font-bold text-lg" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                MACHINERYMARKETED
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Link to="/" className="footer-link text-sm">Home</Link>
              <Link to="/features" className="footer-link text-sm">Features</Link>
              <Link to="/about" className="footer-link text-sm">About</Link>
              <Link to="/faq" className="footer-link text-sm">FAQ</Link>
            </div>

            <p className="text-sm" style={{ color: "var(--text-300)" }}>
              © 2026 MachineryMarketed. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ResourcesPage;
